import React, { useState } from 'react';
import MediaQuery from 'react-responsive';
import { Box } from '@99designs/emu';
import { breakpoints } from '@99designs/emu/settings';
import { IconX1 } from '@99designs/icons';
import { FullscreenHeader } from '@99designs/spa-common';
import { FullscreenHeaderItem } from '@99designs/spa-common';
import { OnWhiteIconButton } from '@99designs/spa-common';
import { AnnotationContext } from '../AnnotationContext';
import { useFileOverlayContext } from '../FileOverlayContext';
import { ScribbleContext } from '../ScribbleContext/ScribbleContext';
import { SelectedEventContext } from '../SelectedEventContext';
import useFileOverlayKeyboardShortcuts from '../useFileOverlayKeyboardShortcuts/useFileOverlayKeyboardShortcuts';
import FeedbackControls from './FeedbackControls/FeedbackControls';
import PaginationNavigation from './PaginationNavigation';
import ViewControls from './ViewControls/ViewControls';
import useIdleTimer from './useIdleTimer/useIdleTimer';

export interface HeaderProps<A, S> {
    totalNumberOfFiles: number;
    currentPageNumber?: number;
    goBack: () => void;
    navNext?: () => void;
    navPrev?: () => void;
    isSidebarHidden: boolean;

    handleMaximize: () => void;
    handleMinimize: () => void;
    isPresentationMode: boolean;

    useSelectedEvent: SelectedEventContext['useSelectedEvent'];
    useAnnotation: AnnotationContext<A>['useAnnotation'];
    useScribble: ScribbleContext<S>['useScribble'];
}

function Header<A, S>({
    totalNumberOfFiles,
    currentPageNumber,
    goBack,
    navNext: navNextBase,
    navPrev: navPrevBase,
    isSidebarHidden,
    handleMaximize,
    handleMinimize,
    isPresentationMode,
    useSelectedEvent,
    useAnnotation,
    useScribble,
}: HeaderProps<A, S>) {
    const { canAnnotate, setAnnotations, setNewAnnotationCoord } = useAnnotation();
    const { setSelectedEventId } = useSelectedEvent();
    const { viewSize, zoomIn, zoomOut, canZoomIn, canZoomOut } = useFileOverlayContext();
    const { scribble, getScribbleLoading, canScribble, canUndoScribble, undoScribble } =
        useScribble(); // TODO: to move into FeedbackControls
    const [userIsIdle, setIdle] = useState(false);

    const navNext = navNextBase
        ? () => {
              setNewAnnotationCoord(null);
              setAnnotations([]);
              setSelectedEventId(null);
              navNextBase();
          }
        : undefined;

    const navPrev = navPrevBase
        ? () => {
              setNewAnnotationCoord(null);
              setAnnotations([]);
              setSelectedEventId(null);
              navPrevBase();
          }
        : undefined;

    const navGoBack = () => {
        setSelectedEventId(null);
        setAnnotations([]);
        goBack();
    };

    useIdleTimer({ enabled: isPresentationMode, setIdle });
    useFileOverlayKeyboardShortcuts({
        isPresentationMode,
        zoomIn,
        zoomOut,
        canZoomIn,
        canZoomOut,
        handleMaximize,
        handleMinimize,
        goBack: navGoBack,
        navNext,
        navPrev,
    });

    return (
        <FullscreenHeader
            inactive={isPresentationMode && userIsIdle}
            designDetails
            onClick={() => {
                setNewAnnotationCoord(null);
                setSelectedEventId(null);
            }}
            data-testid="Header"
        >
            <FullscreenHeaderItem designDetails>
                <PaginationNavigation
                    totalNumberOfPages={totalNumberOfFiles}
                    currentPageNumber={currentPageNumber}
                    navNext={navNext}
                    navPrev={navPrev}
                />
            </FullscreenHeaderItem>
            <MediaQuery maxWidth={breakpoints.normal[0]}>
                {(isMobile) =>
                    isMobile ? (
                        <FullscreenHeaderItem designDetails>
                            <OnWhiteIconButton aria-label={__('Close')} onClick={navGoBack}>
                                <IconX1 />
                            </OnWhiteIconButton>
                        </FullscreenHeaderItem>
                    ) : (
                        <>
                            <FullscreenHeaderItem designDetails>
                                {!isPresentationMode && (
                                    <FeedbackControls
                                        canAnnotate={canAnnotate}
                                        canScribble={canScribble}
                                        isScribbleLoading={getScribbleLoading}
                                        canUndoSketch={canUndoScribble}
                                        undoSketch={undoScribble(scribble)}
                                    />
                                )}
                                {viewSize.height && viewSize.width ? (
                                    <ViewControls
                                        handleMinimize={handleMinimize}
                                        handleMaximize={handleMaximize}
                                        isPresentationMode={isPresentationMode}
                                        zoomIn={zoomIn}
                                        zoomOut={zoomOut}
                                        canZoomIn={canZoomIn}
                                        canZoomOut={canZoomOut}
                                    />
                                ) : null}
                            </FullscreenHeaderItem>
                            {isSidebarHidden && !isPresentationMode && (
                                <FullscreenHeaderItem designDetails>
                                    <Box marginLeft="2bu">
                                        <OnWhiteIconButton
                                            aria-label={__('Close')}
                                            onClick={navGoBack}
                                        >
                                            <IconX1 />
                                        </OnWhiteIconButton>
                                    </Box>
                                </FullscreenHeaderItem>
                            )}
                        </>
                    )
                }
            </MediaQuery>
        </FullscreenHeader>
    );
}

export default Header;
